import { MouseEvent, useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  DiskBackupDateContentsResponse,
  fetchDeleteByBackupDate,
  fetchDiskBackupDateContents,
} from "../../../api/admin";
import { useAuthStore } from "../../../atoms/user.auth.store";
import ContentsTopHeader from "../../../components/common/contents.top.header";
import ModalCenterLayout from "../../../components/modal/modal.center.layout";
import {
  BackupContainer,
  ContentsBody,
} from "../../../style/contents/backup/backup";

type DeleteTarget = {
  rootDirectoryPath: string;
  backupDate: string;
};

type DeleteResult = {
  files: number;
  directories: number;
  histories: number;
};

const getUserAccount = () => {
  const userInfo = localStorage.getItem("userInfo");
  if (!userInfo) return "";

  try {
    const parsed = JSON.parse(userInfo);
    return parsed.userAccount || "";
  } catch {
    return "";
  }
};

const getBackupTypeLabel = (backupType?: string) => {
  if (backupType === "full") return "전체 백업";
  if (backupType === "incremental") return "증분 백업";

  return backupType || "-";
};

const BackupDateSelect = () => {
  const { role } = useAuthStore();
  const [data, setData] = useState<DiskBackupDateContentsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    isVisible: false,
  });
  const [deleteResult, setDeleteResult] = useState<DeleteResult | null>(null);
  const [deleteResultModal, setDeleteResultModal] = useState({
    isOpen: false,
    isVisible: false,
  });

  const location = useLocation();
  const navigate = useNavigate();
  const { path: rawDeviceId = "" } = useParams<{ path: string }>();
  const deviceId = decodeURIComponent(rawDeviceId);
  const queryParams = new URLSearchParams(location.search);
  const userId = queryParams.get("id");
  const queryUserAccount = queryParams.get("userAccount");
  const rootDirectoryPath = queryParams.get("rootDirectoryPath");
  const userAccount =
    role === "user" ? getUserAccount() || queryUserAccount : queryUserAccount;
  const isAdmin = role === "admin";
  const filteredRootBackupDates =
    data?.rootBackupDates.filter(
      (item) => item.rootDirectoryPath === rootDirectoryPath,
    ) || [];

  const handleSearch = () => {
    // TODO: fetchData(keyword, pageSize);
  };

  const handlePageSizeChange = () => {};

  const handleBackupDateClick = (
    rootDirectoryPath: string,
    backupDate: string,
  ) => {
    const encodedRootDirectoryPath = encodeURIComponent(rootDirectoryPath);
    const nextQueryParams = new URLSearchParams(location.search);

    nextQueryParams.set("deviceId", deviceId);
    nextQueryParams.set("backupDate", backupDate);

    localStorage.setItem(
      "directory",
      JSON.stringify({
        directoryPath: rootDirectoryPath,
        createDate: backupDate,
      }),
    );
    localStorage.setItem("initialPath", rootDirectoryPath);

    navigate(
      `/backup/file/list/${encodedRootDirectoryPath}?${nextQueryParams.toString()}`,
    );
  };

  const loadBackupDateContents = useCallback(async () => {
    if (!role) return;
    if (!deviceId || !userId || !userAccount) return;

    try {
      setIsLoading(true);
      const response = await fetchDiskBackupDateContents({
        deviceId,
        userId: Number(userId),
        userAccount,
        isAdmin,
      });
      setData(response);
    } catch {
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, [deviceId, isAdmin, role, userAccount, userId]);

  const handleOpenDeleteModal = (
    event: MouseEvent<HTMLButtonElement>,
    rootDirectoryPath: string,
    backupDate: string,
  ) => {
    event.stopPropagation();
    setDeleteTarget({ rootDirectoryPath, backupDate });
    setDeleteModal({ isOpen: true, isVisible: true });
  };

  const handleCloseDeleteModal = () => {
    setDeleteModal({ isOpen: false, isVisible: false });
    setDeleteTarget(null);
  };

  const handleCloseDeleteResultModal = () => {
    setDeleteResultModal({ isOpen: false, isVisible: false });
    setDeleteResult(null);
  };

  const handleDeleteBackupDate = async () => {
    const adminUserId = localStorage.getItem("userId");

    if (!adminUserId) {
      alert("관리자 정보가 없습니다.");
      return;
    }

    if (!deleteTarget) {
      alert("삭제할 백업 날짜 정보가 없습니다.");
      return;
    }

    try {
      const response = await fetchDeleteByBackupDate(adminUserId, {
        backupDate: deleteTarget.backupDate,
        rootDirectoryPath: deleteTarget.rootDirectoryPath,
      });
      console.log("deleteTarget", deleteTarget);
      console.log("response:", response);

      handleCloseDeleteModal();
      setDeleteResult(response.message);
      setDeleteResultModal({ isOpen: true, isVisible: true });
      loadBackupDateContents();
    } catch (err) {
      console.error("백업 날짜 삭제 실패:", err);
      alert("백업 날짜 삭제 중 오류가 발생했습니다.");
    }
  };

  useEffect(() => {
    loadBackupDateContents();
  }, [loadBackupDateContents]);

  return (
    <BackupContainer>
      <ContentsTopHeader
        title="백업 날짜 선택"
        onSearch={handleSearch}
        onPageSizeChange={handlePageSizeChange}
        pageSizeHidden={true}
        backArrowPath="-1"
      />
      <ContentsBody>
        {isLoading ? (
          <p>불러오는 중입니다.</p>
        ) : filteredRootBackupDates.length === 0 ? (
          <p className="no-data">백업 날짜가 없습니다.</p>
        ) : (
          <div className="backup-date-list">
            {filteredRootBackupDates.map((rootBackupDate) => (
              <div
                className="backup-date-root"
                key={rootBackupDate.rootDirectoryPath}
              >
                <div className="top-components">
                  <div className="l">
                    <label>{rootBackupDate.rootDirectoryPath}</label>
                  </div>
                </div>
                <div className="hr"></div>
                {rootBackupDate.backupDates.map((backupDate) => (
                  <div
                    role="button"
                    tabIndex={0}
                    className="backup-date-item"
                    key={backupDate.backupDate}
                    onClick={() =>
                      handleBackupDateClick(
                        rootBackupDate.rootDirectoryPath,
                        backupDate.backupDate,
                      )
                    }
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        handleBackupDateClick(
                          rootBackupDate.rootDirectoryPath,
                          backupDate.backupDate,
                        );
                      }
                    }}
                  >
                    <p>{backupDate.backupDate}</p>
                    <div className="backup-date-meta">
                      <span>파일 {backupDate.fileCount}개</span>
                      <span>{getBackupTypeLabel(backupDate.backupType)}</span>
                      {isAdmin && (
                        <button
                          type="button"
                          className="backup-date-delete-btn"
                          onClick={(event) =>
                            handleOpenDeleteModal(
                              event,
                              rootBackupDate.rootDirectoryPath,
                              backupDate.backupDate,
                            )
                          }
                        >
                          삭제
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </ContentsBody>
      {deleteModal.isOpen && (
        <ModalCenterLayout
          isVisible={deleteModal.isVisible}
          closedHandler={handleCloseDeleteModal}
          size="s"
        >
          <div className="backup-date-delete-modal">
            <div className="modal-header">
              <h3>백업 날짜 삭제</h3>
              <button
                type="button"
                className="closed-modal-btn"
                onClick={handleCloseDeleteModal}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>해당 백업 날짜를 삭제하시겠습니까?</p>
              {deleteTarget && (
                <p className="delete-target">{deleteTarget.backupDate}</p>
              )}
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="cancel"
                onClick={handleCloseDeleteModal}
              >
                취소
              </button>
              <button
                type="button"
                className="confirm"
                onClick={handleDeleteBackupDate}
              >
                삭제
              </button>
            </div>
          </div>
        </ModalCenterLayout>
      )}
      {deleteResultModal.isOpen && (
        <ModalCenterLayout
          isVisible={deleteResultModal.isVisible}
          closedHandler={handleCloseDeleteResultModal}
          size="s"
        >
          <div className="backup-date-delete-modal">
            <div className="modal-header">
              <h3>삭제 완료</h3>
              <button
                type="button"
                className="closed-modal-btn"
                onClick={handleCloseDeleteResultModal}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>삭제되었습니다.</p>
              {deleteResult && (
                <div className="delete-result">
                  <span>파일: {deleteResult.files}개</span>
                  <span>폴더: {deleteResult.directories}개</span>
                  <span>이력: {deleteResult.histories}개</span>
                </div>
              )}
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="confirm"
                onClick={handleCloseDeleteResultModal}
              >
                확인
              </button>
            </div>
          </div>
        </ModalCenterLayout>
      )}
    </BackupContainer>
  );
};

export default BackupDateSelect;
